/**
 * Пользователи и правила скоупа.
 *
 * Правило скоупа — это ответ на вопрос «какой срез данных человек видит»:
 * управляющий двумя точками, директор одного города, ограничение по глубине
 * истории. Ось отчётов настраивается отдельно, в матрице доступа.
 */

import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { api, ApiError } from '../api'
import type { AdminRole, AdminUser, DimensionValue, ScopeDimension, ScopeRule } from '../types'
import { Badge, Card, CheckChip, Empty, Field, Loading, Notice, Toggle } from '../components/ui'
import { formatDate, formatDateTime, initials, pluralize } from '../format'

export function AdminUsers() {
  const [users, setUsers] = useState<AdminUser[] | null>(null)
  const [roles, setRoles] = useState<AdminRole[]>([])
  const [error, setError] = useState<string | null>(null)
  const [flash, setFlash] = useState<string | null>(null)
  const [creating, setCreating] = useState(false)
  const [query, setQuery] = useState('')

  const reload = async () => {
    const [usersResponse, rolesResponse] = await Promise.all([api.adminUsers(), api.adminRoles()])
    setUsers(usersResponse.users)
    setRoles(rolesResponse.roles)
  }

  useEffect(() => {
    reload().catch((caught) => setError(caught instanceof ApiError ? caught.message : 'Не удалось загрузить'))
  }, [])

  if (error && !users) return <Notice tone="bad">{error}</Notice>
  if (!users) return <Loading rows={6} />

  const needle = query.trim().toLowerCase()
  const visible = needle
    ? users.filter((user) =>
        [user.full_name, user.email, user.position ?? '', ...user.roles.map((role) => role.title)]
          .join(' ')
          .toLowerCase()
          .includes(needle),
      )
    : users

  return (
    <div className="stack" style={{ gap: 18 }}>
      {flash && <Notice tone="info">{flash}</Notice>}
      {error && <Notice tone="bad">{error}</Notice>}

      <div className="row" style={{ alignItems: 'center' }}>
        <input
          className="input catalog-search"
          type="search"
          placeholder="Найти по имени, почте или роли"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          aria-label="Поиск пользователя"
        />
        <span className="spacer" />
        <button className="btn btn--primary" onClick={() => setCreating((value) => !value)}>
          {creating ? 'Отмена' : 'Добавить пользователя'}
        </button>
      </div>

      {creating && (
        <CreateUserForm
          roles={roles}
          onCreated={async (text) => {
            setCreating(false)
            setFlash(text)
            await reload()
          }}
          onError={setError}
        />
      )}

      <Card title={`Пользователи · ${users.length}`} flush>
        {visible.length === 0 ? (
          <Empty title="Никого не нашлось" hint="Измените запрос поиска." />
        ) : (
          <div className="access-list" style={{ padding: '0 16px' }}>
            {visible.map((user) => (
              <div className="access-row" key={user.id} style={{ alignItems: 'center' }}>
                <span className="avatar" style={{ width: 30, height: 30, fontSize: 'var(--text-sm)' }}>
                  {initials(user.full_name)}
                </span>
                <div className="access-row__main">
                  <div className="access-row__title">
                    <Link to={`/admin/users/${user.id}`}>{user.full_name}</Link>
                  </div>
                  <div className="access-row__note">
                    {user.position ?? user.email} · вход: {user.last_login_at ? formatDateTime(user.last_login_at) : 'не входил'}
                  </div>
                  <div className="row row--tight" style={{ marginTop: 4 }}>
                    {user.blocked_at && <Badge tone="bad">заблокирован</Badge>}
                    {user.is_admin && <Badge tone="accent">Администратор</Badge>}
                    {user.can_monitor_ingest && !user.is_admin && <Badge>кабинет загрузки</Badge>}
                    {user.roles.map((role) => (
                      <Badge key={role.id}>{role.title}</Badge>
                    ))}
                    {!user.is_admin && user.roles.length === 0 && <span className="tiny muted">без ролей</span>}
                    {user.scope_rules_count > 0 && (
                      <Badge tone="warn">{pluralize(user.scope_rules_count, 'ограничение', 'ограничения', 'ограничений')}</Badge>
                    )}
                  </div>
                </div>
                <div className="access-row__side">
                  <Link className="btn btn--sm" to={`/admin/users/${user.id}`}>
                    Права
                  </Link>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  )
}

function CreateUserForm({
  roles,
  onCreated,
  onError,
}: {
  roles: AdminRole[]
  onCreated: (message: string) => Promise<void>
  onError: (message: string) => void
}) {
  const [email, setEmail] = useState('')
  const [fullName, setFullName] = useState('')
  const [position, setPosition] = useState('')
  const [roleIds, setRoleIds] = useState<number[]>([])
  const [monitor, setMonitor] = useState(false)
  const [saving, setSaving] = useState(false)
  // Двойной клик уходит одной задачей, раньше чем кнопка успеет стать
  // заблокированной: без этого флага сервер получал бы два запроса.
  const inFlight = useRef(false)

  const submit = async () => {
    if (inFlight.current) return
    inFlight.current = true
    setSaving(true)
    try {
      const result = await api.createUser({
        email: email.trim(),
        full_name: fullName.trim(),
        position: position.trim() || undefined,
        role_ids: roleIds,
        can_monitor_ingest: monitor,
      })
      await onCreated(
        `${fullName} заведён. Пароль: ${result.generated_password} — показан один раз, передайте его лично. ` +
          'Права можно уточнить на его странице.',
      )
    } catch (caught) {
      onError(caught instanceof ApiError ? caught.message : 'Не удалось завести пользователя')
    } finally {
      inFlight.current = false
      setSaving(false)
    }
  }

  return (
    <Card title="Новый пользователь">
      <div className="stack" style={{ gap: 12 }}>
        <div className="form-inline">
          <Field label="Имя и фамилия">
            <input className="input" value={fullName} onChange={(event) => setFullName(event.target.value)} />
          </Field>
          <Field label="Почта — логин">
            <input className="input" type="email" value={email} onChange={(event) => setEmail(event.target.value)} />
          </Field>
          <Field label="Должность">
            <input className="input" value={position} onChange={(event) => setPosition(event.target.value)} />
          </Field>
        </div>
        <div>
          <div className="field__label" style={{ marginBottom: 8 }}>
            Роли
          </div>
          <div className="chip-group">
            {roles.map((role) => {
              const on = roleIds.includes(role.id)
              return (
                <CheckChip
                  key={role.id}
                  checked={on}
                  title={role.description ?? undefined}
                  onClick={() => setRoleIds(on ? roleIds.filter((item) => item !== role.id) : [...roleIds, role.id])}
                >
                  {role.title}
                </CheckChip>
              )
            })}
          </div>
          <div className="tiny muted" style={{ marginTop: 6 }}>
            Без роли человек войдёт, но не увидит ни одного отчёта: по умолчанию закрыто.
          </div>
        </div>
        <Toggle checked={monitor} onChange={setMonitor} label="Кабинет загрузки данных" hint="Для подрядчика 1С: состояние загрузки и итоги по месяцам, без отчётов в разрезах" />
        <div>
          <button className="btn btn--primary" disabled={saving || !email.includes('@') || !fullName.trim()} onClick={submit}>
            {saving ? 'Заводим…' : 'Завести и выдать пароль'}
          </button>
        </div>
      </div>
    </Card>
  )
}

export function describePeriodRule(rule: ScopeRule): string {
  if (rule.period_mode === 'rolling_window') return `последние ${rule.period_days} дней`
  if (rule.period_mode === 'not_before') return `не раньше ${formatDate(rule.period_from)}`
  return 'только закрытые в 1С периоды'
}

export function ScopeRuleForm({
  userId,
  subjectType = 'user',
  dimensions,
  onCreated,
  onError,
}: {
  /** id субъекта: пользователя или роли. */
  userId: string
  subjectType?: 'role' | 'user'
  dimensions: ScopeDimension[]
  onCreated: () => Promise<void>
  onError: (message: string) => void
}) {
  const [dimension, setDimension] = useState('')
  const [mode, setMode] = useState<'allow' | 'deny'>('allow')
  const [values, setValues] = useState<string[]>([])
  const [options, setOptions] = useState<DimensionValue[]>([])
  const [periodMode, setPeriodMode] = useState<'rolling_window' | 'not_before' | 'closed_only'>('rolling_window')
  const [periodDays, setPeriodDays] = useState(180)
  const [periodFrom, setPeriodFrom] = useState('')
  const [saving, setSaving] = useState(false)
  const inFlight = useRef(false)

  useEffect(() => {
    setValues([])
    setOptions([])
    if (!dimension || dimension === 'period') return
    api
      .adminDimensionValues(dimension)
      .then((response) => setOptions(response.values))
      .catch(() => undefined)
  }, [dimension])

  const isPeriod = dimension === 'period'

  const submit = async () => {
    // Второй клик до ответа сервера — тот же запрос ещё раз и дубль правила.
    if (inFlight.current) return
    inFlight.current = true
    setSaving(true)
    try {
      await api.createScopeRule({
        subject_type: subjectType,
        subject_id: userId,
        dimension,
        mode,
        values: isPeriod ? undefined : values,
        period_mode: isPeriod ? periodMode : undefined,
        period_days: isPeriod && periodMode === 'rolling_window' ? periodDays : undefined,
        period_from: isPeriod && periodMode === 'not_before' ? periodFrom : undefined,
      })
      setDimension('')
      setValues([])
      await onCreated()
    } catch (caught) {
      onError(caught instanceof ApiError ? caught.message : 'Не удалось создать правило')
    } finally {
      inFlight.current = false
      setSaving(false)
    }
  }

  return (
    <div className="form-inline">
      <Field label="Измерение">
        <select className="select" value={dimension} onChange={(event) => setDimension(event.target.value)} style={{ minWidth: 200 }}>
          <option value="">— выберите —</option>
          {dimensions.map((item) => (
            <option key={item.code} value={item.code}>
              {item.title}
            </option>
          ))}
        </select>
      </Field>

      {dimension && !isPeriod && (
        <>
          <Field label="Режим">
            <select className="select" value={mode} onChange={(event) => setMode(event.target.value as 'allow' | 'deny')}>
              <option value="allow">только эти значения</option>
              <option value="deny">все, кроме этих</option>
            </select>
          </Field>

          <Field label={`Значения${values.length ? ` · выбрано ${values.length}` : ''}`}>
            <select
              className="select"
              multiple
              size={Math.min(6, Math.max(3, options.length))}
              value={values}
              onChange={(event) =>
                setValues([...event.target.selectedOptions].map((option) => option.value))
              }
              style={{ minWidth: 260 }}
            >
              {options.map((option) => (
                <option key={option.guid} value={option.guid}>
                  {option.name}
                </option>
              ))}
            </select>
          </Field>
        </>
      )}

      {isPeriod && (
        <>
          <Field label="Ограничение">
            <select
              className="select"
              value={periodMode}
              onChange={(event) => setPeriodMode(event.target.value as typeof periodMode)}
              style={{ minWidth: 220 }}
            >
              <option value="rolling_window">скользящее окно, последние N дней</option>
              <option value="not_before">не раньше даты</option>
              <option value="closed_only">только закрытые в 1С периоды</option>
            </select>
          </Field>

          {periodMode === 'rolling_window' && (
            <Field label="Дней">
              <input
                className="input"
                type="number"
                min={1}
                value={periodDays}
                onChange={(event) => setPeriodDays(Number(event.target.value))}
                style={{ width: 100 }}
              />
            </Field>
          )}

          {periodMode === 'not_before' && (
            <Field label="С даты">
              <input className="input" type="date" value={periodFrom} onChange={(event) => setPeriodFrom(event.target.value)} />
            </Field>
          )}
        </>
      )}

      {dimension && (
        <div className="field form-inline__action">
          <span className="field__label" aria-hidden="true">
            &nbsp;
          </span>
          <button className="btn btn--primary" onClick={submit} disabled={saving || (!isPeriod && values.length === 0)} aria-busy={saving}>
            {saving ? 'Сохраняем…' : 'Добавить правило'}
          </button>
        </div>
      )}
    </div>
  )
}
